<template>
    <div
        class="sticker-svg"
        :class="{ 'sticker-svg--loading': loadingFont }"
        :style="boxStyle"
        v-html="svg"
    ></div>
</template>

<script>
import { renderSticker } from "@/lib/sticker/render";
import { getGeometry } from "@/lib/sticker/geometry";
import { loadFont } from "@/lib/sticker/fonts";
import { resolveAsset } from "@/lib/sticker/print";

const aspectCache = {};

function imageAspect(href) {
    if (!href) {
        return Promise.resolve(1);
    }
    if (aspectCache[href]) {
        return Promise.resolve(aspectCache[href]);
    }
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            const aspect =
                img.naturalWidth > 0 && img.naturalHeight > 0
                    ? img.naturalWidth / img.naturalHeight
                    : 1;
            aspectCache[href] = aspect;
            resolve(aspect);
        };
        img.onerror = () => resolve(1);
        img.src = href;
    });
}

export default {
    name: "StickerSvg",
    props: {
        sticker: { type: Object, required: true },
        design: { type: Object, required: true },
        figureHref: { type: String, default: null },
        backgroundHref: { type: String, default: null },
        maxHeight: { type: Number, default: null }
    },
    data() {
        return {
            font: null,
            loadingFont: false,
            figureAspect: 1,
            figureAsset: null,
            backgroundAsset: null
        };
    },
    computed: {
        boxStyle() {
            const aspect = getGeometry(this.sticker).aspectRatio;
            const style = { aspectRatio: String(aspect) };
            if (this.maxHeight) {
                style.maxWidth = `${Math.round(this.maxHeight * aspect)}px`;
                style.margin = "0 auto";
            }
            return style;
        },
        svg() {
            return renderSticker(this.sticker, this.design, {
                mode: "preview",
                font: this.font,
                figureAspect: this.figureAspect,
                uid: `sticker-${this._uid}`,
                assets: {
                    figure: this.asset(this.figureHref, this.figureAsset),
                    background: this.asset(
                        this.backgroundHref,
                        this.backgroundAsset
                    )
                }
            });
        }
    },
    watch: {
        "design.selectedFont": {
            immediate: true,
            handler(family) {
                this.loadingFont = true;
                loadFont(family).then(font => {
                    if (family === this.design.selectedFont) {
                        this.font = font;
                        this.loadingFont = false;
                    }
                });
            }
        },
        figureHref: {
            immediate: true,
            handler(href) {
                this.figureAsset = null;
                imageAspect(href).then(aspect => {
                    if (href === this.figureHref) {
                        this.figureAspect = aspect;
                    }
                });
                resolveAsset(href).then(asset => {
                    if (href === this.figureHref) {
                        this.figureAsset = asset;
                    }
                });
            }
        },
        backgroundHref: {
            immediate: true,
            handler(href) {
                this.backgroundAsset = null;
                resolveAsset(href).then(asset => {
                    if (href === this.backgroundHref) {
                        this.backgroundAsset = asset;
                    }
                });
            }
        }
    },
    methods: {
        // Use the resolved (inlined) asset once ready, the plain URL until then.
        asset(href, resolved) {
            if (!href) {
                return null;
            }
            return resolved || { href };
        }
    }
};
</script>

<style>
.sticker-svg {
    width: 100%;
    line-height: 0;
    transition: opacity 0.2s;
}
.sticker-svg svg {
    width: 100%;
    height: 100%;
    display: block;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12));
}
.sticker-svg--loading {
    opacity: 0.85;
}
</style>
